/**
 * Subject families drive lesson-tile colours in the timetable grid.
 *
 * Ported verbatim from the authoring app's `src/utils/subjectFamily.ts` so the
 * two front ends colour a lesson the same way. The RULES below are the shared
 * contract — change them in both repos or the grids drift apart.
 *
 * The app returns Tailwind class names here; the portal cannot, because it is
 * themed with CSS variables and has a dark mode the app does not. So this
 * module returns the family key alone and the colours live in index.css as
 * --fam-* tokens, defined in both themes.
 *
 *   lang  — English, Arabic, French, Setswana, reading…
 *   math  — Maths, Statistics, Mechanics
 *   sci   — sciences, computing, D&T, robotics, food & nutrition
 *   hum   — humanities, business and commerce, GC, Islamic subjects
 *   opt   — an option block: several subjects running at once
 *   other — anything unrecognised
 */
export type SubjectFamily = 'lang' | 'math' | 'sci' | 'hum' | 'opt' | 'other';

const RULES: Array<{ family: Exclude<SubjectFamily, 'opt' | 'other'>; re: RegExp }> = [
  { family: 'math', re: /\b(math|maths|mathematics|statistics|mechanics|further\s*math)/ },
  {
    family: 'lang',
    re: /\b(english|arabic|french|setswana|spanish|german|mandarin|language|literature|reading|rea)(\b|_|\d)/,
  },
  {
    family: 'sci',
    re: /\b(physics|chemistry|biology|science|ict|computer|comp\.?\s*sci|computing|robotics|d\s*&\s*t|dnt|design|food|nutrition|agric)/,
  },
  {
    family: 'hum',
    re: /\b(history|geography|economics|business|accounting|accounts|travel|tourism|env\.?\s*mng|environmental|sociology|psychology|religious|islamic|gc|general\s*paper|civic|art|music|drama|pe\b|physical|quraan|quran|hifz|fiqh|aqaaid|hadeeth|duas)/,
  },
];

export function subjectFamily(subject: string | undefined | null): SubjectFamily {
  if (!subject) return 'other';
  const s = subject.toLowerCase().replace(/_/g, ' ');
  for (const r of RULES) if (r.re.test(s)) return r.family;
  return 'other';
}

export const FAMILY_LABELS: Record<SubjectFamily, string> = {
  lang: 'Languages',
  math: 'Maths',
  sci: 'Sciences & tech',
  hum: 'Humanities & Islamic',
  opt: 'Option blocks',
  other: 'Other',
};

export const LEGEND_FAMILIES: SubjectFamily[] = ['lang', 'math', 'sci', 'hum', 'opt'];
